import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import useDocument from "../../hooks/useDocument";
import { getId } from "../../utils/idHelpers";
import useApi from "../../hooks/useApi";

const DocumentViewerModal = ({
  open,
  onClose,
  fileUrl,
  fileName,
  document,
}) => {
  const { deleteDocument, updateDocumentMetadata, replaceDocumentFile } =
    useDocument();
  const { getData } = useApi();
  const [fileType, setFileType] = useState("");
  const [text, setText] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [replacementFile, setReplacementFile] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [folderPath, setFolderPath] = useState("");
  const [folderMode, setFolderMode] = useState("new");
  const [selectedExistingFolder, setSelectedExistingFolder] = useState("");
  const [existingFolders, setExistingFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);

  const normalizeFolderPath = (value) => {
    return String(value || "")
      .trim()
      .replace(/\\/g, "/")
      .replace(/\/+/g, "/")
      .replace(/^\/+|\/+$/g, "");
  };

  const effectiveFolderPath =
    folderMode === "existing"
      ? normalizeFolderPath(selectedExistingFolder)
      : normalizeFolderPath(folderPath);

  useEffect(() => {
    setName(document?.name || "");
    setDescription(document?.description || "");
    const initialFolderPath = normalizeFolderPath(
      document?.folderPath || document?.category || "",
    );
    setFolderPath(initialFolderPath);
    setSelectedExistingFolder(initialFolderPath);
  }, [document?.id, document?.key]);

  useEffect(() => {
    const loadExistingFolders = async () => {
      if (!open || !document?.scope) return;

      setLoadingFolders(true);
      try {
        const filter = { scope: document.scope, folder: "documents" };
        if (document.scope === "client") filter.clientId = document.clientId;
        if (document.scope === "project") filter.projectId = document.projectId;

        const docs = await getData({
          entityName: "Document",
          filter,
        });

        if (!docs?.isSuccessful || !Array.isArray(docs?.data)) {
          setExistingFolders([]);
          return;
        }

        const folderSet = new Set();
        for (const doc of docs.data) {
          const normalized = normalizeFolderPath(
            doc?.folderPath || doc?.category || "",
          );
          if (normalized) folderSet.add(normalized);
        }

        const folderList = Array.from(folderSet).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: "base" }),
        );

        setExistingFolders(folderList);

        if (folderList.length === 0) {
          setFolderMode("new");
          return;
        }

        const currentPath = normalizeFolderPath(
          document?.folderPath || document?.category || "",
        );

        if (folderList.includes(currentPath)) {
          setSelectedExistingFolder(currentPath);
        } else if (!normalizeFolderPath(selectedExistingFolder)) {
          setSelectedExistingFolder(folderList[0]);
        }
      } catch (err) {
        console.error("Failed to load existing document folders:", err);
        setExistingFolders([]);
      } finally {
        setLoadingFolders(false);
      }
    };

    loadExistingFolders();
  }, [open, document?.scope, document?.clientId, document?.projectId, document?.id]);

  useEffect(() => {
    if (fileUrl) {
      if (
        [".jpg", ".jpeg", ".png", ".gif", ".webp"].includes(document.extension)
      ) {
        setFileType("image");
      } else if ([".txt", ".json", ".csv"].includes(document.extension)) {
        fetch(fileUrl)
          .then((res) => res.text())
          .then(setText);

        setFileType("text");
      } else {
        setFileType("other");
      }
    }
  }, [fileUrl]);

  const handleDelete = async () => {
    const docId = getId(document);

    if (!docId) {
      alert("Document ID not found");
      return;
    }

    setDeleting(true);
    const result = await deleteDocument(docId);
    setDeleting(false);

    if (result.isSuccessful) {
      setConfirmDelete(false);
      onClose(true); // Pass true to indicate deletion
    } else {
      alert(`Failed to delete document: ${result.message || "Unknown error"}`);
    }
  };

  const handleSaveMetadata = async () => {
    if (!document) return;

    setSaving(true);
    const result = await updateDocumentMetadata(document, {
      name,
      description,
      folderPath: effectiveFolderPath,
    });
    setSaving(false);

    if (result?.isSuccessful) {
      onClose({ updated: true });
      return;
    }

    alert(`Failed to update document: ${result?.message || "Unknown error"}`);
  };

  const handleReplaceFileChange = (event) => {
    const file = event?.target?.files?.[0] || null;
    setReplacementFile(file);
  };

  const handleReplaceFile = async () => {
    if (!document || !replacementFile) return;

    setReplacing(true);
    const result = await replaceDocumentFile(document, replacementFile, {
      name,
      description,
      folderPath: effectiveFolderPath,
    });
    setReplacing(false);

    if (result?.isSuccessful) {
      setReplacementFile(null);
      onClose({ updated: true });
      return;
    }

    alert(`Failed to replace document: ${result?.message || "Unknown error"}`);
  };

  const renderContent = () => {
    if (fileType === "image") {
      return (
        <img
          src={fileUrl}
          alt={document.name}
          style={{ maxWidth: "100%" }}
        />
      );
    }
    if (fileType === "text") {
      return <pre style={{ whiteSpace: "pre-wrap" }}>{text}</pre>;
    }
    return (
      <Typography>
        Preview not supported.{" "}
        <a
          href={fileUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Download {document.name}
        </a>
      </Typography>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>{document?.name || fileName || "Document"}</DialogTitle>
      <DialogContent>
        <Stack
          spacing={2}
          sx={{ mb: 2, mt: 1 }}
        >
          <TextField
            fullWidth
            label="Document Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            fullWidth
            label="Description"
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <TextField
            fullWidth
            label="Folder"
            value={folderPath}
            onChange={(e) => setFolderPath(e.target.value)}
            disabled={folderMode === "existing"}
          />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={folderMode}
            onChange={(_e, nextMode) => {
              if (!nextMode) return;
              setFolderMode(nextMode);
            }}
          >
            <ToggleButton value="new">New Folder</ToggleButton>
            <ToggleButton
              value="existing"
              disabled={existingFolders.length === 0}
            >
              Existing Folder
            </ToggleButton>
          </ToggleButtonGroup>
          {folderMode === "existing" && (
            <FormControl
              fullWidth
              disabled={loadingFolders || existingFolders.length === 0}
            >
              <InputLabel id="edit-existing-folder-label">
                Existing Folder
              </InputLabel>
              <Select
                labelId="edit-existing-folder-label"
                label="Existing Folder"
                value={selectedExistingFolder}
                onChange={(e) => setSelectedExistingFolder(e.target.value)}
              >
                {existingFolders.map((path) => (
                  <MenuItem
                    key={path}
                    value={path}
                  >
                    {path}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Button
              variant="outlined"
              component="label"
              disabled={deleting || saving || replacing}
            >
              Choose Replacement File
              <input
                type="file"
                hidden
                onChange={handleReplaceFileChange}
              />
            </Button>
            <Typography variant="body2">
              {replacementFile?.name || "No replacement file selected"}
            </Typography>
          </Stack>
        </Stack>
        {renderContent()}
      </DialogContent>
      <DialogActions>
        {!confirmDelete ? (
          <>
            <Button
              onClick={onClose}
              disabled={deleting || saving || replacing}
            >
              Close
            </Button>
            <Button
              onClick={handleSaveMetadata}
              variant="contained"
              disabled={
                deleting ||
                saving ||
                replacing ||
                (folderMode === "existing" && !effectiveFolderPath)
              }
            >
              {saving ? "Saving..." : "Save"}
            </Button>
            <Button
              onClick={handleReplaceFile}
              variant="contained"
              disabled={
                !replacementFile ||
                deleting ||
                saving ||
                replacing ||
                (folderMode === "existing" && !effectiveFolderPath)
              }
            >
              {replacing ? "Replacing..." : "Replace File"}
            </Button>
            <Button
              onClick={() => setConfirmDelete(true)}
              color="error"
              disabled={deleting || saving || replacing}
            >
              Delete
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={() => setConfirmDelete(false)}
              disabled={deleting || saving || replacing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              color="error"
              variant="contained"
              disabled={deleting || saving || replacing}
            >
              {deleting ? "Deleting..." : "Confirm Delete"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DocumentViewerModal;
